import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleDollarSign, Gauge, PauseCircle, PlayCircle, Wallet } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtInt, fmtUsd } from '@/lib/format';
import { CostTimeseriesChart } from '@/components/CostTimeseriesChart';
import { EmptyState } from '@/components/EmptyState';
import { Masthead } from '@/components/Masthead';
import { SkeletonLine } from '@/components/Skeleton';
import { StatTile } from '@/components/StatTile';
import { Button } from '@/components/ui/Button';

function defaultMonth(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

type ChartRange = '7d' | '90d';

export default function AgentCosts() {
  const [month, setMonth] = useState(defaultMonth());
  const [chartRange, setChartRange] = useState<ChartRange>('7d');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const qc = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['agents-cost', month],
    queryFn: () => api.agentsCost(month),
  });

  const pauseStatus = useQuery({
    queryKey: ['pause-status'],
    queryFn: api.pauseStatus,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const pauseMutation = useMutation({
    mutationFn: api.pauseAgents,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pause-status'] });
      setConfirmOpen(false);
    },
  });
  const resumeMutation = useMutation({
    mutationFn: api.resumeAgents,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pause-status'] }),
  });
  const paused = pauseStatus.data?.paused ?? false;

  const totalCost = data?.agents.reduce((s, a) => s + a.cost_usd, 0) ?? 0;
  const totalBudget = data?.agents.reduce((s, a) => s + a.budget_usd, 0) ?? 0;
  const utilization = totalBudget > 0 ? (totalCost / totalBudget) * 100 : 0;

  const utilTone: 'brand' | 'warn' | 'danger' =
    utilization > 100 ? 'danger' : utilization > 80 ? 'warn' : 'brand';

  return (
    <div className="flex flex-col gap-6">
      <Masthead
        kicker="Operations"
        title="Agent ledger"
        dateline={`Accounting period · ${month}`}
        lede="Token consumption, operating cost, and budget posture for every agent in the pipeline — a transparent accounting for the cost of autonomy."
        actions={
          <>
            <label className="flex items-center gap-2 rounded-lg border border-rule bg-surface px-3 py-1.5 text-[0.8125rem] text-ink-muted shadow-card">
              <span className="font-semibold">Month</span>
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="cursor-pointer border-0 bg-transparent text-ink focus:outline-none"
              />
            </label>
            {paused ? (
              <Button
                variant="secondary"
                onClick={() => resumeMutation.mutate()}
                disabled={resumeMutation.isPending}
              >
                <PlayCircle className="h-4 w-4" />
                Resume all agents
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={() => setConfirmOpen(true)}
                disabled={pauseMutation.isPending}
              >
                <PauseCircle className="h-4 w-4" />
                Pause all agents
              </Button>
            )}
          </>
        }
      />

      {confirmOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 px-4"
          onClick={() => setConfirmOpen(false)}
        >
          <div
            className="card max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[1rem] font-semibold text-ink">Pause all agents?</div>
            <p className="mt-2 text-[0.875rem] text-ink-muted">
              No agent will start new work until you resume. In-flight batches will finish.
              You'll see a banner at the top of every page while paused.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => pauseMutation.mutate()}
                disabled={pauseMutation.isPending}
              >
                Pause now
              </Button>
            </div>
          </div>
        </div>
      )}

      {isError && (
        <div className="rounded-xl border border-danger-border bg-danger-soft px-5 py-4 text-sm text-danger-bold">
          Failed to load ledger: {error instanceof Error ? error.message : 'unknown'}
        </div>
      )}

      {/* Summary tiles */}
      <section aria-label="Ledger summary" className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatTile
          label="Month spend"
          value={fmtUsd(totalCost)}
          hint="Realised cost, all agents"
          icon={CircleDollarSign}
          tone="brand"
        />
        <StatTile
          label="Month budget"
          value={fmtUsd(totalBudget)}
          hint="Ceiling across agents"
          icon={Wallet}
        />
        <StatTile
          label="Utilization"
          value={`${utilization.toFixed(0)}%`}
          hint={utilization > 100 ? 'Over budget' : utilization > 80 ? 'Approaching cap' : 'Under budget'}
          icon={Gauge}
          tone={utilTone}
        />
      </section>

      {/* Utilization bar */}
      {totalBudget > 0 && (
        <div className="card flex flex-col gap-2 p-5">
          <div className="flex items-center justify-between text-[0.8125rem]">
            <span className="font-semibold text-ink">Overall utilization</span>
            <span className="font-mono tabular-nums text-ink-muted">
              {fmtUsd(totalCost)} / {fmtUsd(totalBudget)}
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-surface-sunk">
            <div
              className={
                'h-full transition-all ' +
                (utilization > 100
                  ? 'bg-danger'
                  : utilization > 80
                    ? 'bg-warn'
                    : 'bg-brand-500')
              }
              style={{ width: `${Math.min(utilization, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Time-series chart */}
      <section aria-label="Cost over time" className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-[0.9375rem] font-semibold text-ink">Cost trend</h2>
          <div className="flex items-center gap-1 rounded-lg border border-rule bg-surface p-0.5 text-[0.75rem]">
            {(['7d', '90d'] as ChartRange[]).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setChartRange(r)}
                className={
                  'rounded-md px-2.5 py-1 font-semibold transition-colors ' +
                  (chartRange === r
                    ? 'bg-brand-50 text-brand-800'
                    : 'text-ink-muted hover:text-ink')
                }
              >
                {r === '7d' ? 'Last 7 days' : 'Last 90 days'}
              </button>
            ))}
          </div>
        </div>
        <CostTimeseriesChart
          granularity={chartRange === '7d' ? 'hour' : 'day'}
          days={chartRange === '7d' ? 7 : 90}
        />
      </section>

      {/* By-agent table */}
      <section aria-label="By agent" className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-[0.9375rem] font-semibold text-ink">By agent · {month}</h2>
          <div className="text-[0.75rem] text-ink-muted">
            {isLoading ? 'Loading…' : `${data?.agents.length ?? 0} agents`}
          </div>
        </div>

        {isLoading ? (
          <div className="card flex flex-col gap-3 p-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonLine key={i} width={`${70 + ((i * 9) % 25)}%`} />
            ))}
          </div>
        ) : (data?.agents.length ?? 0) === 0 ? (
          <EmptyState title="No runs booked this period" />
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="e-table w-full">
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th className="text-right">Runs</th>
                    <th className="text-right">Tokens in</th>
                    <th className="text-right">Tokens out</th>
                    <th className="text-right">Cost</th>
                    <th className="text-right">Budget</th>
                    <th className="w-48">Utilization</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.agents ?? []).map((a) => (
                    <tr key={a.name}>
                      <td className="text-[0.8125rem] font-semibold text-ink">{a.name}</td>
                      <td className="text-right font-mono text-[0.8125rem] tabular-nums">
                        {fmtInt(a.runs)}
                      </td>
                      <td className="text-right font-mono text-[0.8125rem] tabular-nums text-ink-muted">
                        {fmtInt(a.tokens_in)}
                      </td>
                      <td className="text-right font-mono text-[0.8125rem] tabular-nums text-ink-muted">
                        {fmtInt(a.tokens_out)}
                      </td>
                      <td className="text-right font-mono text-[0.8125rem] font-semibold tabular-nums text-ink">
                        {fmtUsd(a.cost_usd)}
                      </td>
                      <td className="text-right font-mono text-[0.8125rem] tabular-nums text-ink-muted">
                        {fmtUsd(a.budget_usd)}
                      </td>
                      <td>
                        <UsageBar pct={a.budget_used_pct} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function UsageBar({ pct }: { pct: number }) {
  const clamped = Math.min(Math.max(pct, 0), 200);
  const tone =
    pct > 100 ? 'danger' : pct > 80 ? 'warn' : 'brand';
  const barColor =
    tone === 'danger' ? 'bg-danger' : tone === 'warn' ? 'bg-warn' : 'bg-brand-500';
  const textColor =
    tone === 'danger' ? 'text-danger-bold' : tone === 'warn' ? 'text-warn-bold' : 'text-brand-700';
  return (
    <div className="flex items-center gap-3">
      <div className="h-2 w-28 overflow-hidden rounded-full bg-surface-sunk">
        <div className={barColor + ' h-full'} style={{ width: `${Math.min(clamped, 100)}%` }} />
      </div>
      <span className={'font-mono text-[0.75rem] font-semibold tabular-nums ' + textColor}>
        {pct.toFixed(0)}%
      </span>
    </div>
  );
}
